import React from 'react';
import BaseModel from '../BaseModel';
import { Utils } from '../../services';

/** Base workflow brick; subclasses extend this. Factory: `Brick.make` in Brick.js. */
class Brick extends BaseModel {
    /**
     * Run this brick
     *
     * @param {Object} context
     * @param {Boolean} isEmbedded
     * @param {Callable} onDone
     */
    run(_context, _isEmbedded, _onDone) {
        return <div className={this.type} />;
    }

    /**
     * Get this brick options form
     *
     * @param {Object} props: an object with the following keys
     *                          - t
     */
    getOptionsForm(_props) {
        return null;
    }

    /**
     * Must this brick run server side?
     *
     * @return {Boolean}
     **/
    isServerSide() {
        return !!this.server_side;
    }

    /**
     * Used by BrickForm for the inputs_mapping tab — must reflect {@link this.inputs}.
     *
     * @return {String[]}
     **/
    getInputs() {
        return Array.isArray(this.inputs) ? this.inputs : [];
    }

    /**
     * Get this brick inputs mapping
     *
     * @return {Object}
     **/
    getInputsMapping() {
        return this.inputs_mapping;
    }

    /**
     * Get one of this brick inputs mapping
     *
     * @return {String}
     **/
    getInputMapping(input) {
        return this.inputs_mapping[input] ?? null;
    }

    /**
     * Get this brick outputs
     *
     * @return {String[]}
     **/
    getOutputs() {
        return Array.isArray(this.outputs) ? this.outputs : [];
    }

    /**
     * Get one of this brick option value
     *
     * @param {String} option
     * @param {Any} _default
     *
     * @return {Any}
     */
    getOption(option, _default) {
        return this.options?.[option] ?? _default;
    }

    /**
     * @param {Object|null|undefined} detailCtx
     * @returns {string}
     */
    getDetailLang(detailCtx) {
        return detailCtx?.lang || Utils.default_lang();
    }

    /**
     * @param {unknown} value
     * @param {Object|null|undefined} detailCtx
     * @param {string} [fallback]
     * @returns {string}
     */
    localizedDetailLabel(value, detailCtx, fallback = '') {
        if (value == null)
            return fallback;
        if (typeof value === 'string')
            return value;
        if (typeof value === 'object')
            return Utils.localized_or_fallback(value, this.getDetailLang(detailCtx), fallback) || fallback;
        return String(value);
    }

    /**
     * @param {Array<unknown>} parts
     * @returns {string|null}
     */
    joinNodeDetailParts(parts) {
        const cleaned = (parts || []).filter((p) => p != null && String(p).trim() !== '');
        if (!cleaned.length)
            return null;
        return cleaned.join(' · ');
    }

    /**
     * @param {unknown} value
     * @param {number} [max_len]
     * @returns {string|null}
     */
    truncateNodeDetail(value, max_len = 52) {
        if (value == null)
            return null;
        const text = String(value).trim();
        if (!text)
            return null;
        return text.length > max_len ? `${text.slice(0, max_len - 3)}...` : text;
    }

    /**
     * Optional summary shown under a brick label in the workflow graph.
     *
     * @param {Object} _detail_ctx linked resources and ui context
     * @returns {string|null}
     */
    getNodeDetails(_detail_ctx) {
        return null;
    }
}

export default Brick;
